/**
 * Authentication API Hooks
 * React Query hooks for authentication operations
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import AxiosClient from '@/api/client';
import { Endpoints } from '@/config/endpoints';
import { useAuth } from '@/providers/AuthProvider';
import { toast } from '@/hooks/use-toast';
import type {
  LoginRequest,
  LoginResponse,
  ItemResponse,
  UserProfile,
  CreateUserRequest,
} from '@/types/api';

// ============================================================================
// Query Keys
// ============================================================================

export const authQueryKeys = {
  profile: ['auth', 'profile'] as const,
} as const;

// ============================================================================
// Login Mutation
// ============================================================================

interface UseLoginMutationOptions {
  onSuccessCallback?: (data: LoginResponse) => void;
  onErrorCallback?: (error: string) => void;
}

export function useLoginMutation(options: UseLoginMutationOptions = {}) {
  const { login } = useAuth();
  const { onSuccessCallback, onErrorCallback } = options;

  return useMutation({
    mutationFn: async (
      credentials: LoginRequest
    ): Promise<ItemResponse<LoginResponse>> => {
      const client = new AxiosClient<LoginRequest, LoginResponse>({
        endpoint: Endpoints.LOGIN,
        contentType: 'application/json',
        isToken: false, // Don't send auth token for login
      });

      const response = await client.post(credentials);

      if (!response.status) {
        // Throw the full response to preserve detail and error fields
        const error = new Error(
          response.detail || response.error || 'Login failed'
        );
        (error as any).response = { data: response };
        throw error;
      }

      return response;
    },

    onSuccess: response => {
      if (response.data) {
        // Extract user and tokens from response
        const { profile, tokens } = response.data;

        // Update auth context
        login(tokens, profile);

        toast({
          title: 'Login Successful',
          description: `Welcome back, ${profile.name}!`,
        });

        // Call optional success callback
        onSuccessCallback?.(response.data);
      }
    },

    onError: (error: any) => {
      console.error('Login error:', error);

      let errorMessage = 'Login failed. Please try again.';

      // Check if error has response data (from our custom error or axios error)
      if (error.response?.data) {
        const errorData = error.response.data as ItemResponse<unknown>;
        // Prioritize detail over error for more specific messages
        errorMessage = errorData.detail || errorData.error || errorMessage;
      } else if (error.message) {
        // Fallback to error message if no response data
        errorMessage = error.message;
      }

      toast({
        title: 'Login Failed',
        description: errorMessage,
        variant: 'destructive',
      });

      // Call optional error callback
      onErrorCallback?.(errorMessage);
    },
  });
}

// ============================================================================
// Logout Mutation
// ============================================================================

export function useLogoutMutation() {
  const { logout, tokens } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (): Promise<ItemResponse<string>> => {
      if (!tokens?.access) {
        // If no token, just perform local logout
        return {
          data: 'Logged out locally',
          status: true,
          error: null,
          detail: 'No active session',
        };
      }

      const client = new AxiosClient<undefined, string>({
        endpoint: Endpoints.LOGOUT,
        contentType: 'application/json',
        isToken: true,
      });

      return await client.post();
    },

    onSuccess: () => {
      // Clear all queries from cache
      queryClient.clear();

      // Perform logout (clears auth state and redirects)
      logout();

      toast({
        title: 'Logged Out',
        description: 'You have been successfully logged out.',
      });
    },

    onError: (error: AxiosError) => {
      console.error('Logout error:', error);

      // Even if API logout fails, perform local logout
      queryClient.clear();
      logout();

      toast({
        title: 'Logged Out',
        description: 'Session ended.',
      });
    },
  });
}

// ============================================================================
// Profile Query
// ============================================================================

export function useProfileQuery() {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: authQueryKeys.profile,
    queryFn: async (): Promise<UserProfile> => {
      const client = new AxiosClient<undefined, UserProfile>({
        endpoint: Endpoints.PROFILE,
        contentType: 'application/json',
        isToken: true,
      });

      const response = await client.getItem();

      if (!response.status) {
        throw new Error(response.error || 'Failed to fetch profile');
      }

      return response.data;
    },
    enabled: isAuthenticated, // Only run if user is authenticated
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: (failureCount, error) => {
      // Don't retry on 401 errors (token expired)
      if (error instanceof AxiosError && error.response?.status === 401) {
        return false;
      }
      return failureCount < 2;
    },
  });
}

// ============================================================================
// Register Mutation (Admin only)
// ============================================================================

interface UseRegisterMutationOptions {
  onSuccessCallback?: () => void;
}

export function useRegisterMutation(options: UseRegisterMutationOptions = {}) {
  const queryClient = useQueryClient();
  const { onSuccessCallback } = options;

  return useMutation({
    mutationFn: async (
      userData: CreateUserRequest
    ): Promise<ItemResponse<UserProfile>> => {
      const client = new AxiosClient<CreateUserRequest, UserProfile>({
        endpoint: Endpoints.REGISTER,
        contentType: 'application/json',
        isToken: true, // Requires authentication (admin only)
      });

      const response = await client.post(userData);

      if (!response.status) {
        // Throw the entire response object so we can access it in onError
        const error: any = new Error(response.error || 'Registration failed');
        error.response = { data: response };
        throw error;
      }

      return response;
    },

    onSuccess: response => {
      toast({
        title: window.i18n?.t('users.toasts.userCreatedTitle') || 'User Created',
        description: window.i18n?.t('users.toasts.userCreatedDesc', { name: response.data.name }) || `${response.data.name} has been successfully created.`,
      });

      // Invalidate users list to refresh data
      queryClient.invalidateQueries({ queryKey: ['users'] });

      onSuccessCallback?.();
    },

    onError: (error: any) => {
      console.error('Registration error:', error);

      let errorMessage = 'Failed to create user. Please try again.';

      // Check if error has response data attached
      const errorData = error.response?.data;

      if (errorData) {
        // Check if detail contains validation errors (object with field-specific errors)
        if (
          errorData.detail &&
          typeof errorData.detail === 'object' &&
          !Array.isArray(errorData.detail)
        ) {
          // Extract all validation messages from the object
          const validationErrors: string[] = [];
          Object.entries(errorData.detail).forEach(([field, messages]) => {
            if (Array.isArray(messages)) {
              messages.forEach((msg: string) => validationErrors.push(msg));
            } else if (typeof messages === 'string') {
              validationErrors.push(messages);
            }
          });

          if (validationErrors.length > 0) {
            // Join errors with bullet points
            errorMessage = validationErrors.map(msg => `• ${msg}`).join('\n');
          }
        } else if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        } else if (errorData.error) {
          errorMessage = errorData.error;
        }
      }

      toast({
        title: 'Registration Failed',
        description: errorMessage,
        variant: 'destructive',
      });
    },
  });
}

// ============================================================================
// Utility Hooks
// ============================================================================

/**
 * Hook to check if user is currently logging in
 */
export function useIsLoggingIn() {
  const loginMutation = useLoginMutation();
  return loginMutation.isPending;
}

/**
 * Hook to check if user is currently logging out
 */
export function useIsLoggingOut() {
  const logoutMutation = useLogoutMutation();
  return logoutMutation.isPending;
}
